import { useState, useEffect, useMemo, useRef } from 'react';
import {
  ClipboardList,
  Plus,
  Search,
  Loader2,
  X,
  MapPin,
  Calendar,
  Play,
  CheckCircle2,
  Ban,
  Trash2,
  Pencil,
  Camera,
  PenLine,
  AlertTriangle,
  Clock,
  Phone,
  Mail,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Check,
  FileDown,
  Send,
  Save,
  Eye,
  Paperclip,
  FileText,
  Image,
  Bell,
  Share2,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ShareLinkModal from '../components/ShareLinkModal';
import {
  missionsApi,
  driversApi,
  devicesApi,
  type Mission,
  type MissionStatus,
  type MissionType,
  type CreateMissionPayload,
} from '../services/api';
import { useAuthStore } from '../store/auth.store';

const STATUS_COLORS: Record<MissionStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 border-gray-200',
  SCHEDULED: 'bg-blue-100 text-blue-700 border-blue-200',
  IN_PROGRESS: 'bg-amber-100 text-amber-800 border-amber-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
};

const TYPE_KEYS: MissionType[] = ['INTERVENTION', 'DELIVERY', 'PICKUP', 'INSPECTION', 'OTHER'];

type FilterStatus = MissionStatus | 'ALL' | 'ARCHIVED';

interface SimpleDriver {
  id: string;
  name: string;
  isActive: boolean;
  missionsEnabled: boolean;
}

interface SimpleDevice {
  id: string;
  name: string;
  plate: string | null;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function driverName(d: { firstName?: string | null; lastName?: string | null } | null | undefined): string {
  if (!d) return '';
  return `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim();
}

/**
 * Format a duration in milliseconds as a human-friendly French string:
 * "1 h 23 min", "45 min", "12 sec".
 */
function formatDuration(ms: number | undefined | null): string {
  if (!ms || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`;
  if (m > 0) return `${m} min`;
  return `${s} sec`;
}

/**
 * Compact mission card used inside the weekly resource grid (one per cell).
 * Click → opens the detail drawer in the parent.
 */
function MissionCell({ mission, onClick }: { mission: Mission; onClick: () => void }) {
  const time = mission.scheduledAt
    ? new Date(mission.scheduledAt).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-1.5 py-1 rounded border text-[10px] hover:shadow-sm transition-all ${STATUS_COLORS[mission.status]}`}
      title={`${mission.reference} — ${mission.title}${mission.address ? ' · ' + mission.address : ''}`}
    >
      <div className="flex items-center gap-1 font-semibold">
        <Clock className="w-2.5 h-2.5 shrink-0" />
        <span>{time}</span>
        <span className="font-mono text-[8px] opacity-70 truncate">{mission.reference}</span>
      </div>
      <p className="font-medium truncate leading-tight">{mission.title}</p>
    </button>
  );
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

const DAY_KEYS = ['day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat', 'day_sun'] as const;

type ViewMode = 'list' | 'planning';

export default function MissionsPage() {
  const currentUser = useAuthStore((s) => s.user);
  // Helper: is the currently authenticated user the assigned driver of this mission?
  // Used to gate Start / Save-draft / Finalize buttons — only the actual driver
  // can perform these actions, dispatchers cannot do them on behalf.
  const isAssignedDriver = (m: Mission | null): boolean => {
    if (!m || !currentUser) return false;
    return m.assignedDriverId === currentUser.id;
  };

  const [missions, setMissions] = useState<Mission[]>([]);
  const [drivers, setDrivers] = useState<SimpleDriver[]>([]);
  const [devices, setDevices] = useState<SimpleDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('ALL');
  // Multi-row selection — drives the bulk action bar (archive/unarchive/delete).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [supportError, setSupportError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [weekStart, setWeekStart] = useState<Date>(() => getMonday(new Date()));

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Mission | null>(null);
  const [detailMission, setDetailMission] = useState<Mission | null>(null);

  // PDF report state
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [sendPdfMission, setSendPdfMission] = useState<Mission | null>(null);
  const [sendPdfRecipient, setSendPdfRecipient] = useState('');
  const [sendPdfMessage, setSendPdfMessage] = useState('');
  const [sendPdfBusy, setSendPdfBusy] = useState(false);
  const [shareLinkMission, setShareLinkMission] = useState<Mission | null>(null);
  const { t } = useTranslation();

  // Form state
  const [formData, setFormData] = useState<CreateMissionPayload>({
    title: '',
    type: 'INTERVENTION',
    address: '',
  });
  // Extra editable fields shown only when editing a COMPLETED mission
  // (typo-fix corrections that propagate to the PDF report)
  const [editCompletionNotes, setEditCompletionNotes] = useState('');
  const [editSignerName, setEditSignerName] = useState('');
  const [saving, setSaving] = useState(false);
  const [formAttachments, setFormAttachments] = useState<string[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  // Detail / completion state
  const [busy, setBusy] = useState<string | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [signerName, setSignerName] = useState('');
  const [proofPhotos, setProofPhotos] = useState<string[]>([]);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);

  // Load data
  useEffect(() => {
    loadMissions();
    // Drop the selection whenever the visible set changes — selecting rows in
    // one filter and then jumping to another would otherwise act on invisible IDs.
    setSelectedIds(new Set());
  }, [statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadSupportData();
  }, []);

  const loadMissions = async () => {
    setLoading(true);
    try {
      const isArchivedView = statusFilter === 'ARCHIVED';
      const data = await missionsApi.list({
        // The "Archivées" filter is a synthetic status; map it to the
        // archived scope and let the backend skip the status filter.
        status: statusFilter === 'ALL' || isArchivedView ? undefined : statusFilter,
        archived: isArchivedView ? 'archived' : 'active',
        from: dateFrom ? `${dateFrom}T00:00:00.000Z` : undefined,
        to: dateTo ? `${dateTo}T23:59:59.999Z` : undefined,
      });
      setMissions(data);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('missions.error_load'));
    } finally {
      setLoading(false);
    }
  };

  const loadSupportData = async () => {
    try {
      const [driversRes, devicesRes] = await Promise.allSettled([
        // NB: /api/drivers caps limit at 100 server-side (validation)
        driversApi.list({ isActive: true, limit: 100 }),
        devicesApi.list({ limit: 200 }),
      ]);

      if (driversRes.status === 'fulfilled') {
        const driversPayload =
          driversRes.value.data?.data ||
          (Array.isArray(driversRes.value.data) ? driversRes.value.data : []) ||
          [];
        setDrivers(Array.isArray(driversPayload) ? driversPayload : []);
      } else {
        console.error('[MissionsPage] loadSupportData drivers failed', driversRes.reason);
        const err = driversRes.reason as { response?: { data?: { error?: string } } };
        setSupportError(
          t('missions.load_drivers_failed', {
            error: err.response?.data?.error || t('missions.unknown_error'),
          }),
        );
      }

      if (devicesRes.status === 'fulfilled') {
        const devicesPayload =
          devicesRes.value.data?.data ||
          devicesRes.value.data?.devices ||
          (Array.isArray(devicesRes.value.data) ? devicesRes.value.data : []) ||
          [];
        setDevices(Array.isArray(devicesPayload) ? devicesPayload : []);
      } else {
        console.error('[MissionsPage] loadSupportData devices failed', devicesRes.reason);
        const err = devicesRes.reason as { response?: { data?: { error?: string } } };
        setSupportError(
          t('missions.load_vehicles_failed', {
            error: err.response?.data?.error || t('missions.unknown_error'),
          }),
        );
      }
    } catch (e) {
      console.error('[MissionsPage] loadSupportData unexpected error', e);
    }
  };

  // Filter missions by search text (client-side)
  const filteredMissions = useMemo(() => {
    if (!search) return missions;
    const q = search.toLowerCase();
    return missions.filter(
      (m) =>
        m.reference.toLowerCase().includes(q) ||
        m.title.toLowerCase().includes(q) ||
        m.address.toLowerCase().includes(q) ||
        (m.clientName?.toLowerCase().includes(q) ?? false) ||
        driverName(m.assignedDriver).toLowerCase().includes(q),
    );
  }, [missions, search]);

  const counts = useMemo(() => {
    const base: Record<MissionStatus, number> = {
      DRAFT: 0,
      SCHEDULED: 0,
      IN_PROGRESS: 0,
      COMPLETED: 0,
      CANCELLED: 0,
    };
    for (const m of missions) base[m.status]++;
    return base;
  }, [missions]);

  // ---------- Create / Edit ----------

  const openCreateForm = () => {
    setEditing(null);
    setFormData({
      title: '',
      type: 'INTERVENTION',
      address: '',
    });
    setFormAttachments([]);
    setShowForm(true);
  };

  const openEditForm = (m: Mission) => {
    setEditing(m);
    setFormData({
      title: m.title,
      description: m.description ?? undefined,
      type: m.type,
      address: m.address,
      city: m.city ?? undefined,
      postalCode: m.postalCode ?? undefined,
      country: m.country ?? undefined,
      clientName: m.clientName ?? undefined,
      clientContact: m.clientContact ?? undefined,
      clientPhone: m.clientPhone ?? undefined,
      clientEmail: m.clientEmail ?? undefined,
      scheduledAt: m.scheduledAt,
      estimatedDurationMin: m.estimatedDurationMin,
      priority: m.priority,
      assignedDeviceId: m.assignedDeviceId,
      assignedDriverId: m.assignedDriverId,
    });
    setFormAttachments(m.attachmentUrls ?? []);
    // For completed missions, also load the completion fields so a dispatcher
    // can fix typos in notes / signer name. These propagate to the next PDF.
    setEditCompletionNotes(m.completionNotes ?? '');
    setEditSignerName(m.signerName ?? '');
    setShowForm(true);
  };

  const handleSave = async (notify: boolean = false) => {
    if (!formData.title.trim() || !formData.address.trim()) {
      alert(t('missions.title_address_required'));
      return;
    }
    setSaving(true);
    try {
      const payload: CreateMissionPayload = {
        ...formData,
        scheduledAt: formData.scheduledAt
          ? new Date(formData.scheduledAt).toISOString()
          : null,
        attachmentUrls: formAttachments.length > 0 ? formAttachments : undefined,
      };
      if (editing) {
        // Assignment goes through a dedicated endpoint — extract it from the
        // update payload and send a separate PATCH /assign only if it changed.
        const { assignedDeviceId, assignedDriverId, ...editable } = payload;
        const editPayload: typeof editable & {
          completionNotes?: string | null;
          signerName?: string | null;
          notify?: boolean;
        } = { ...editable };
        if (editing.status === 'COMPLETED') {
          editPayload.completionNotes = editCompletionNotes || null;
          editPayload.signerName = editSignerName || null;
        }
        // Reassign if driver or device changed (and status allows it). When
        // both update and assign fire, only the assign call carries `notify`
        // so the driver gets exactly one push instead of two.
        const assignmentChanged =
          assignedDriverId !== editing.assignedDriverId ||
          assignedDeviceId !== editing.assignedDeviceId;
        const assignmentEditable =
          editing.status !== 'COMPLETED' && editing.status !== 'CANCELLED';
        const willReassign = assignmentChanged && assignmentEditable;

        editPayload.notify = notify && !willReassign;
        await missionsApi.update(editing.id, editPayload);

        if (willReassign) {
          await missionsApi.assign(editing.id, {
            assignedDriverId: assignedDriverId ?? null,
            assignedDeviceId: assignedDeviceId ?? null,
            notify,
          });
        }

        // Refresh the open detail drawer if it's the same mission
        if (detailMission?.id === editing.id) {
          const updated = await missionsApi.get(editing.id);
          setDetailMission(updated);
        }
      } else {
        await missionsApi.create({ ...payload, notify });
      }
      setShowForm(false);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_save'));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Actions ----------

  const handleDelete = async (m: Mission) => {
    if (!confirm(t('missions.delete_confirm', { ref: m.reference }))) return;
    try {
      await missionsApi.delete(m.id);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_delete'));
    }
  };

  const handleToggleArchive = async (m: Mission) => {
    const willArchive = !m.archivedAt;
    const confirmKey = willArchive ? 'missions.confirm_archive' : 'missions.confirm_unarchive';
    if (!confirm(t(confirmKey, { ref: m.reference }))) return;
    try {
      if (willArchive) {
        await missionsApi.archive(m.id);
      } else {
        await missionsApi.unarchive(m.id);
      }
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_save'));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (rows: Mission[]) => {
    if (rows.length === 0) return;
    const allSelected = rows.every((m) => selectedIds.has(m.id));
    setSelectedIds(allSelected ? new Set() : new Set(rows.map((m) => m.id)));
  };

  const handleBulkArchive = async (archive: boolean) => {
    if (selectedIds.size === 0) return;
    const confirmKey = archive ? 'missions.bulk_confirm_archive' : 'missions.bulk_confirm_unarchive';
    if (!confirm(t(confirmKey, { count: selectedIds.size }))) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      const op = archive ? missionsApi.archive : missionsApi.unarchive;
      await Promise.all(ids.map((id) => op(id)));
      setSelectedIds(new Set());
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_save'));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(t('missions.bulk_confirm_delete', { count: selectedIds.size }))) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => missionsApi.delete(id)));
      setSelectedIds(new Set());
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_delete'));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleAssign = async (m: Mission, driverId: string | null, deviceId: string | null) => {
    setBusy(m.id);
    try {
      const updated = await missionsApi.assign(m.id, {
        assignedDriverId: driverId,
        assignedDeviceId: deviceId,
      });
      setDetailMission(updated);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_assign'));
    } finally {
      setBusy(null);
    }
  };

  const handleStart = async (m: Mission) => {
    setBusy(m.id);
    try {
      const updated = await missionsApi.start(m.id);
      setDetailMission(updated);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_start'));
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async (m: Mission) => {
    const reason = prompt(t('missions.cancel_reason_prompt'), '');
    if (reason === null) return;
    setBusy(m.id);
    try {
      const updated = await missionsApi.cancel(m.id, reason || undefined);
      setDetailMission(updated);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_cancel'));
    } finally {
      setBusy(null);
    }
  };

  // ---------- Completion (proof) ----------

  const openCompletionForm = (m: Mission) => {
    setDetailMission(m);
    setShowCompletionForm(true);
    setCompletionNotes(m.completionNotes ?? '');
    setSignerName(m.signerName ?? '');
    setProofPhotos(m.proofPhotoUrls ?? []);
    setSignatureDataUrl(null);
  };

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingAttachment(true);
    try {
      for (const file of Array.from(files)) {
        const result = await missionsApi.uploadProof(file);
        setFormAttachments((prev) => [...prev, result.url]);
      }
    } catch {
      alert(t('missions.error_upload_attachment'));
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const removeAttachment = (url: string) => {
    setFormAttachments((prev) => prev.filter((u) => u !== url));
  };

  const getAttachmentName = (url: string) => {
    const parts = url.split('/');
    const filename = parts[parts.length - 1] || '';
    // UUID is 36 chars, followed by '-', then original filename
    return filename.length > 37 ? filename.substring(37) : filename;
  };

  const isImageUrl = (url: string) => /\.(jpe?g|png|webp)$/i.test(url);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      for (const file of Array.from(files)) {
        const result = await missionsApi.uploadProof(file);
        setProofPhotos((prev) => [...prev, result.url]);
      }
    } catch (err) {
      alert(t('missions.error_upload_photo'));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeProofPhoto = (url: string) => {
    setProofPhotos((prev) => prev.filter((u) => u !== url));
  };

  // Signature canvas — draw on mobile/desktop
  useEffect(() => {
    if (!showCompletionForm || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1f2937';
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [showCompletionForm]);

  const getCanvasPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = 'touches' in e ? e.touches[0]!.clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0]!.clientY : (e as React.MouseEvent).clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    drawingRef.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getCanvasPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getCanvasPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    drawingRef.current = false;
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureDataUrl(null);
  };

  /**
   * Extract the signature image from the canvas as an uploaded URL.
   * Returns the existing signature if no new ink was drawn, or null if neither.
   */
  const captureSignatureFromCanvas = async (): Promise<string | null> => {
    let signatureUrl: string | null = signatureDataUrl;
    if (canvasRef.current && !signatureUrl) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d')!;
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let hasInk = false;
      for (let i = 0; i < imageData.data.length; i += 4) {
        const r = imageData.data[i]!;
        const g = imageData.data[i + 1]!;
        const b = imageData.data[i + 2]!;
        if (r < 250 || g < 250 || b < 250) {
          hasInk = true;
          break;
        }
      }
      if (hasInk) {
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, 'image/png'),
        );
        if (blob) {
          const file = new File([blob], 'signature.png', { type: 'image/png' });
          const result = await missionsApi.uploadProof(file);
          signatureUrl = result.url;
        }
      }
    }
    // Fallback to the existing signature if any (don't drop it on draft save)
    if (!signatureUrl && detailMission?.signatureUrl) {
      signatureUrl = detailMission.signatureUrl;
    }
    return signatureUrl;
  };

  const handleSaveDraft = async () => {
    if (!detailMission) return;
    setBusy(detailMission.id);
    try {
      const signatureUrl = await captureSignatureFromCanvas();
      const updated = await missionsApi.saveDraft(detailMission.id, {
        notes: completionNotes || null,
        signerName: signerName || null,
        signatureUrl,
        proofPhotoUrls: proofPhotos,
      });
      setDetailMission(updated);
      setShowCompletionForm(false);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_save_draft'));
    } finally {
      setBusy(null);
    }
  };

  const handleComplete = async () => {
    if (!detailMission) return;
    if (!confirm(t('missions.finalize_confirm'))) {
      return;
    }
    setBusy(detailMission.id);
    try {
      const signatureUrl = await captureSignatureFromCanvas();
      const updated = await missionsApi.complete(detailMission.id, {
        notes: completionNotes || null,
        signerName: signerName || null,
        signatureUrl,
        proofPhotoUrls: proofPhotos,
      });
      setDetailMission(updated);
      setShowCompletionForm(false);
      await loadMissions();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_complete'));
    } finally {
      setBusy(null);
    }
  };

  // ---------- PDF report ----------

  const handleViewPdf = async (m: Mission) => {
    setPdfDownloading(true);
    try {
      await missionsApi.viewPdfInBrowser(m.id);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_view_pdf'));
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleDownloadPdf = async (m: Mission) => {
    setPdfDownloading(true);
    try {
      const blob = await missionsApi.downloadPdf(m.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-${m.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_download_pdf'));
    } finally {
      setPdfDownloading(false);
    }
  };

  const openSendPdfModal = (m: Mission) => {
    setSendPdfMission(m);
    setSendPdfRecipient(m.clientEmail ?? '');
    setSendPdfMessage('');
  };

  const handleSendPdf = async () => {
    if (!sendPdfMission) return;
    if (!sendPdfRecipient.trim() || !/^\S+@\S+\.\S+$/.test(sendPdfRecipient)) {
      alert(t('missions.error_invalid_email'));
      return;
    }
    setSendPdfBusy(true);
    try {
      const result = await missionsApi.sendPdf(sendPdfMission.id, {
        to: sendPdfRecipient.trim(),
        message: sendPdfMessage.trim() || undefined,
      });
      alert(t('missions.report_sent_to', { recipient: result.recipient }));
      setSendPdfMission(null);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || t('missions.error_send_pdf'));
    } finally {
      setSendPdfBusy(false);
    }
  };

  // ---------- Render ----------

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-primary-600" /> {t('missions.header_title')}
          </h1>
          <p className="text-sm text-gray-500">
            {t('missions.header_subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 ${
                viewMode === 'list' ? 'bg-primary-500 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ClipboardList className="w-4 h-4" /> {t('missions.view_list')}
            </button>
            <button
              onClick={() => setViewMode('planning')}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 border-l border-gray-200 ${
                viewMode === 'planning' ? 'bg-primary-500 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Calendar className="w-4 h-4" /> {t('missions.view_planning')}
            </button>
          </div>
          <button
            onClick={openCreateForm}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium shadow-sm"
          >
            <Plus className="w-4 h-4" /> {t('missions.new_mission')}
          </button>
        </div>
      </div>

      {/* Support data error banner */}
      {supportError && (
        <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="flex-1">
            {t('missions.support_error_suffix', { message: supportError })}
          </span>
          <button
            onClick={() => {
              setSupportError(null);
              loadSupportData();
            }}
            className="text-amber-900 underline"
          >
            {t('missions.retry')}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {(['ALL', 'DRAFT', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'ARCHIVED'] as FilterStatus[]).map(
          (s) => {
            const active = statusFilter === s;
            const label = s === 'ALL'
              ? t('missions.filter_all')
              : s === 'ARCHIVED'
                ? t('missions.filter_archived')
                : t(`missions.status.${s}`);
            // Counts only apply within the active list. When viewing the
            // archived scope the per-status counts are irrelevant; render
            // only the total count on the "Archivées" pill.
            const count = statusFilter === 'ARCHIVED'
              ? (s === 'ARCHIVED' ? missions.length : 0)
              : s === 'ALL'
                ? missions.length
                : s === 'ARCHIVED'
                  ? 0
                  : counts[s as MissionStatus];
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300'
                }`}
              >
                {label}
                {count > 0 && <span className="ml-1 font-semibold opacity-80">({count})</span>}
              </button>
            );
          },
        )}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('missions.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <span className="text-gray-500">{t('missions.date_from_label')}</span>
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-900"
            title={t('missions.filter_date_from_title')}
          />
          <span className="text-gray-500">{t('missions.date_to_label')}</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-900"
            title={t('missions.filter_date_to_title')}
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="px-2 py-1.5 text-gray-500 hover:text-gray-700"
              title={t('missions.filter_date_clear')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* List view */}
      {viewMode === 'list' && loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : viewMode === 'list' && filteredMissions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 mb-3">{t('missions.empty_list')}</p>
          <button
            onClick={openCreateForm}
            className="text-sm bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg"
          >
            {t('missions.empty_create')}
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Bulk action bar — visible only when at least one row is selected */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap px-4 py-2 bg-primary-50 border-b border-primary-200">
              <span className="text-xs font-medium text-primary-900 mr-2">
                {t('missions.bulk_selected', { count: selectedIds.size })}
              </span>
              {statusFilter === 'ARCHIVED' ? (
                <button
                  onClick={() => handleBulkArchive(false)}
                  disabled={bulkBusy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                >
                  <ArchiveRestore className="w-3.5 h-3.5" />
                  {t('missions.bulk_unarchive', { count: selectedIds.size })}
                </button>
              ) : (
                <button
                  onClick={() => handleBulkArchive(true)}
                  disabled={bulkBusy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                >
                  <Archive className="w-3.5 h-3.5" />
                  {t('missions.bulk_archive', { count: selectedIds.size })}
                </button>
              )}
              <button
                onClick={handleBulkDelete}
                disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t('missions.bulk_delete', { count: selectedIds.size })}
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                disabled={bulkBusy}
                className="ml-auto text-xs text-primary-700 hover:underline disabled:opacity-50"
              >
                {t('missions.bulk_clear')}
              </button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 tracking-wide">
                <tr>
                  <th className="px-4 py-2 w-10">
                    <input
                      type="checkbox"
                      aria-label={t('missions.bulk_select_all')}
                      checked={filteredMissions.length > 0 && filteredMissions.every((m) => selectedIds.has(m.id))}
                      onChange={() => toggleSelectAll(filteredMissions)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="text-left px-4 py-2 font-medium">{t('missions.th_ref')}</th>
                  <th className="text-left px-4 py-2 font-medium">{t('missions.th_title')}</th>
                  <th className="text-left px-4 py-2 font-medium">{t('missions.th_status')}</th>
                  <th className="text-left px-4 py-2 font-medium">{t('missions.th_scheduled')}</th>
                  <th className="text-left px-4 py-2 font-medium">{t('missions.th_driver')}</th>
                  <th className="text-left px-4 py-2 font-medium">{t('missions.th_vehicle')}</th>
                  <th className="text-right px-4 py-2 font-medium">{t('missions.th_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredMissions.map((m) => (
                  <tr
                    key={m.id}
                    className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedIds.has(m.id) ? 'bg-primary-50/50' : ''}`}
                    onClick={() => setDetailMission(m)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={t('missions.bulk_select_row')}
                        checked={selectedIds.has(m.id)}
                        onChange={() => toggleSelect(m.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{m.reference}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 line-clamp-1">{m.title}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 line-clamp-1">
                        <MapPin className="w-3 h-3" /> {m.address}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[m.status]}`}
                      >
                        {t(`missions.status.${m.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {formatDateTime(m.scheduledAt)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      {driverName(m.assignedDriver) || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      {m.assignedDevice ? (
                        <span>
                          {m.assignedDevice.name}
                          {m.assignedDevice.plate && (
                            <span className="text-gray-400 ml-1">({m.assignedDevice.plate})</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {m.status !== 'CANCELLED' && !m.archivedAt && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditForm(m);
                            }}
                            className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
                            title={
                              m.status === 'COMPLETED'
                                ? t('missions.tooltip_edit_completed')
                                : t('missions.tooltip_edit_mission')
                            }
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleArchive(m);
                          }}
                          className="text-xs px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded"
                          title={m.archivedAt ? t('missions.action_unarchive') : t('missions.action_archive')}
                        >
                          {m.archivedAt ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(m);
                          }}
                          className="text-xs px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Planning view — weekly resource grid (one row per driver, 7 day columns) */}
      {viewMode === 'planning' && (() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Build the 7 days of the displayed week
        const weekDays: Date[] = Array.from({ length: 7 }).map((_, i) => {
          const d = new Date(weekStart);
          d.setDate(d.getDate() + i);
          d.setHours(0, 0, 0, 0);
          return d;
        });
        const weekEnd = new Date(weekDays[6]!);
        weekEnd.setHours(23, 59, 59, 999);

        // Index missions by (driverId | 'unassigned') × dayKey
        type DayBucket = Map<string, Mission[]>;
        const byDriver = new Map<string, DayBucket>();
        const ensureBucket = (key: string): DayBucket => {
          let b = byDriver.get(key);
          if (!b) {
            b = new Map();
            byDriver.set(key, b);
          }
          return b;
        };

        for (const m of filteredMissions) {
          if (!m.scheduledAt) continue;
          const d = new Date(m.scheduledAt);
          if (d < weekDays[0]! || d > weekEnd) continue;
          const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const driverKey = m.assignedDriverId ?? '__unassigned__';
          const bucket = ensureBucket(driverKey);
          const list = bucket.get(dayKey) ?? [];
          list.push(m);
          bucket.set(dayKey, list);
        }
        // Sort each cell by time
        for (const bucket of byDriver.values()) {
          for (const list of bucket.values()) {
            list.sort((a, b) => {
              const ta = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0;
              const tb = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0;
              return ta - tb;
            });
          }
        }

        // Build the row list: active drivers with a Mission seat + unassigned bucket if non-empty
        const driverRows = drivers
          .filter((d) => d.isActive && d.missionsEnabled)
          .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        const hasUnassigned = (byDriver.get('__unassigned__')?.size ?? 0) > 0;

        return (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Week navigator */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  const d = new Date(weekStart);
                  d.setDate(d.getDate() - 7);
                  setWeekStart(d);
                }}
                className="p-1.5 hover:bg-gray-200 rounded"
                title={t('missions.prev_week')}
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <div className="text-sm font-semibold text-gray-900">
                {t('missions.week_of', {
                  from: weekDays[0]!.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }),
                  to: weekDays[6]!.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
                })}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setWeekStart(getMonday(new Date()))}
                  className="text-xs px-2 py-1 hover:bg-gray-200 rounded text-gray-700"
                >
                  {t('missions.today')}
                </button>
                <button
                  onClick={() => {
                    const d = new Date(weekStart);
                    d.setDate(d.getDate() + 7);
                    setWeekStart(d);
                  }}
                  className="p-1.5 hover:bg-gray-200 rounded"
                  title={t('missions.next_week')}
                >
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : driverRows.length === 0 && !hasUnassigned ? (
              <div className="text-center py-16 text-gray-500 text-sm">
                {t('missions.no_drivers_with_seat_html')}<a href="/drivers" className="text-primary-600 underline">/drivers</a>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="sticky left-0 z-10 bg-gray-50 border-r border-b border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide w-[180px] min-w-[180px]"
                      >
                        {t('missions.th_planning_driver')}
                      </th>
                      {weekDays.map((day, idx) => {
                        const isToday = day.getTime() === today.getTime();
                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                        return (
                          <th
                            key={idx}
                            scope="col"
                            className={`border-r border-b border-gray-200 px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide ${
                              isToday
                                ? 'bg-primary-100/60 text-primary-800'
                                : isWeekend
                                  ? 'bg-gray-100/60 text-gray-600'
                                  : 'text-gray-600'
                            }`}
                            style={{ minWidth: 130 }}
                          >
                            <div>{t(`missions.${DAY_KEYS[idx]}`)}</div>
                            <div className="text-base font-bold mt-0.5">{day.getDate()}</div>
                            <div className="text-[9px] font-normal opacity-70">
                              {day.toLocaleDateString('fr-FR', { month: 'short' })}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Unassigned row (only if there are missions without driver) */}
                    {hasUnassigned && (
                      <tr className="bg-amber-50/30">
                        <td className="sticky left-0 z-10 bg-amber-50 border-r border-b border-amber-200 px-3 py-2 align-top">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                            <span className="text-xs font-semibold text-amber-800">{t('missions.unassigned')}</span>
                          </div>
                        </td>
                        {weekDays.map((day, idx) => {
                          const dayKey = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
                          const cellMissions = byDriver.get('__unassigned__')?.get(dayKey) ?? [];
                          return (
                            <td
                              key={idx}
                              className="border-r border-b border-amber-200 align-top p-1 min-h-[60px]"
                            >
                              <div className="space-y-1">
                                {cellMissions.map((m) => (
                                  <MissionCell
                                    key={m.id}
                                    mission={m}
                                    onClick={() => setDetailMission(m)}
                                  />
                                ))}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    )}

                    {/* One row per active driver */}
                    {driverRows.map((driver) => {
                      const driverBucket = byDriver.get(driver.id);
                      return (
                        <tr key={driver.id} className="hover:bg-gray-50/40">
                          <td className="sticky left-0 z-10 bg-white border-r border-b border-gray-200 px-3 py-2 align-top group-hover:bg-gray-50/40">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {driver.name}
                            </div>
                          </td>
                          {weekDays.map((day, idx) => {
                            const dayKey = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
                            const cellMissions = driverBucket?.get(dayKey) ?? [];
                            const isToday = day.getTime() === today.getTime();
                            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                            return (
                              <td
                                key={idx}
                                className={`border-r border-b border-gray-200 align-top p-1 min-h-[80px] ${
                                  isToday ? 'bg-primary-50/30' : isWeekend ? 'bg-gray-50/30' : ''
                                }`}
                              >
                                {cellMissions.length === 0 ? (
                                  <button
                                    onClick={() => {
                                      setEditing(null);
                                      const scheduled = new Date(day);
                                      scheduled.setHours(9, 0, 0, 0);
                                      setFormData({
                                        title: '',
                                        type: 'INTERVENTION',
                                        address: '',
                                        scheduledAt: scheduled.toISOString(),
                                        assignedDriverId: driver.id,
                                      });
                                      setShowForm(true);
                                    }}
                                    className="w-full h-full min-h-[60px] flex items-center justify-center text-gray-300 hover:text-primary-600 hover:bg-primary-50/50 rounded transition-colors"
                                    title={t('missions.create_mission_for_on', {
                                      driver: driver.name,
                                      date: day.toLocaleDateString('fr-FR'),
                                    })}
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                ) : (
                                  <div className="space-y-1">
                                    {cellMissions.map((m) => (
                                      <MissionCell
                                        key={m.id}
                                        mission={m}
                                        onClick={() => setDetailMission(m)}
                                      />
                                    ))}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer reminder for missions outside the displayed week */}
            {(() => {
              const offWeek = filteredMissions.filter((m) => {
                if (!m.scheduledAt) return m.status !== 'COMPLETED' && m.status !== 'CANCELLED';
                const d = new Date(m.scheduledAt);
                return d < weekDays[0]! || d > weekEnd;
              });
              if (offWeek.length === 0) return null;
              return (
                <div className="border-t border-gray-200 bg-gray-50/60 px-4 py-2 text-xs text-gray-500">
                  {t('missions.off_week_count', { count: offWeek.length })}
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Create / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">{editing ? t('missions.modal_edit_title', { ref: editing.reference }) : t('missions.modal_new_title')}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_title_label')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('missions.field_title_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_type')}</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as MissionType })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    {TYPE_KEYS.map((tk) => (
                      <option key={tk} value={tk}>
                        {t(`missions.type.${tk}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_priority')}</label>
                  <select
                    value={formData.priority ?? 0}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value={-1}>{t('missions.priority_low')}</option>
                    <option value={0}>{t('missions.priority_normal')}</option>
                    <option value={1}>{t('missions.priority_high')}</option>
                    <option value={2}>{t('missions.priority_urgent')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_description')}</label>
                <textarea
                  rows={2}
                  value={formData.description ?? ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-y"
                />
              </div>

              <hr className="border-gray-200" />
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('missions.section_location')}</h4>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_address_label')}</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder={t('missions.field_address_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_city')}</label>
                  <input
                    type="text"
                    value={formData.city ?? ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_postal_code')}</label>
                  <input
                    type="text"
                    value={formData.postalCode ?? ''}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_country')}</label>
                  <input
                    type="text"
                    value={formData.country ?? ''}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>

              <hr className="border-gray-200" />
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('missions.section_client')}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_client_name')}</label>
                  <input
                    type="text"
                    value={formData.clientName ?? ''}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_client_contact')}</label>
                  <input
                    type="text"
                    value={formData.clientContact ?? ''}
                    onChange={(e) => setFormData({ ...formData, clientContact: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_client_phone')}</label>
                  <input
                    type="tel"
                    value={formData.clientPhone ?? ''}
                    onChange={(e) => setFormData({ ...formData, clientPhone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_client_email')}</label>
                  <input
                    type="email"
                    value={formData.clientEmail ?? ''}
                    onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>

              <hr className="border-gray-200" />
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('missions.section_planning')}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_scheduled_at')}</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduledAt ? formData.scheduledAt.slice(0, 16) : ''}
                    onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value || null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.field_estimated_duration')}</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.estimatedDurationMin ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimatedDurationMin: e.target.value ? parseInt(e.target.value, 10) : null,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>

              {/* Attachments (photos / PDFs) */}
              <hr className="border-gray-200" />
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t('missions.section_attachments')}
              </h4>
              <div>
                <input
                  ref={attachmentInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  multiple
                  onChange={handleAttachmentUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={uploadingAttachment}
                  onClick={() => attachmentInputRef.current?.click()}
                  className="flex items-center gap-2 text-sm px-3 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors w-full justify-center"
                >
                  {uploadingAttachment ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Paperclip className="w-4 h-4" />
                  )}
                  {t('missions.add_attachments')}
                </button>
                {formAttachments.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {formAttachments.map((url) => (
                      <div
                        key={url}
                        className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                      >
                        {isImageUrl(url) ? (
                          <Image className="w-4 h-4 text-blue-500 shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 text-red-500 shrink-0" />
                        )}
                        <span className="text-gray-700 truncate flex-1">
                          {getAttachmentName(url)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(url)}
                          className="text-gray-400 hover:text-red-500 shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {(!editing || editing.status !== 'COMPLETED') && (
                <>
                  <hr className="border-gray-200" />
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {editing ? t('missions.section_assignment') : t('missions.section_assignment_optional')}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('missions.driver')}{' '}
                        {drivers.length > 0 && (
                          <span className="text-gray-400 font-normal">
                            {t('missions.field_driver_with_seat_count', { count: drivers.filter((d) => d.isActive && d.missionsEnabled).length })}
                          </span>
                        )}
                      </label>
                      <select
                        value={formData.assignedDriverId ?? ''}
                        onChange={(e) =>
                          setFormData({ ...formData, assignedDriverId: e.target.value || null })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                      >
                        <option value="">—</option>
                        {drivers.filter((d) => d.isActive && d.missionsEnabled).length === 0 && (
                          <option value="" disabled>
                            {t('missions.no_drivers_with_seat_option')}
                          </option>
                        )}
                        {drivers
                          .filter((d) => d.isActive && d.missionsEnabled)
                          .map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('missions.vehicle')}{' '}
                        {devices.length > 0 && (
                          <span className="text-gray-400 font-normal">{t('missions.vehicle_count', { count: devices.length })}</span>
                        )}
                      </label>
                      <select
                        value={formData.assignedDeviceId ?? ''}
                        onChange={(e) =>
                          setFormData({ ...formData, assignedDeviceId: e.target.value || null })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                      >
                        <option value="">—</option>
                        {devices.length === 0 && (
                          <option value="" disabled>
                            {t('missions.no_vehicles_option')}
                          </option>
                        )}
                        {devices.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name} {d.plate && `(${d.plate})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Completion fields — visible only when editing a COMPLETED mission.
                  Allows dispatchers to fix typos that will appear in the PDF. */}
              {editing && editing.status === 'COMPLETED' && (
                <>
                  <hr className="border-gray-200" />
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                    <strong>{t('missions.correction_warning_title')}</strong>{t('missions.correction_warning_body')}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('missions.field_completion_notes')}
                    </label>
                    <textarea
                      rows={4}
                      value={editCompletionNotes}
                      onChange={(e) => setEditCompletionNotes(e.target.value)}
                      placeholder={t('missions.completion.notes_placeholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-y"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('missions.field_signer_name')}
                    </label>
                    <input
                      type="text"
                      value={editSignerName}
                      onChange={(e) => setEditSignerName(e.target.value)}
                      placeholder={t('missions.signer_name_placeholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 flex flex-wrap justify-end gap-2 shadow-[0_-4px_8px_-4px_rgba(0,0,0,0.05)]">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg"
              >
                {t('missions.btn_cancel')}
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white px-6 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 shadow-sm"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {editing ? t('missions.save_changes') : t('missions.save_mission')}
              </button>
              {formData.assignedDriverId && (
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  title={t('missions.notify_button_title')}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-6 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 shadow-sm"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Bell className="w-4 h-4" />
                  )}
                  {t('missions.save_and_notify')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {detailMission && !showCompletionForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-end" onClick={() => setDetailMission(null)}>
          <div
            className="bg-white w-full max-w-xl h-full shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                    {detailMission.reference}
                  </span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[detailMission.status]}`}>
                    {t(`missions.status.${detailMission.status}`)}
                  </span>
                  <span className="text-[10px] text-gray-500">{t(`missions.type.${detailMission.type}`)}</span>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">{detailMission.title}</h2>
              </div>
              <button onClick={() => setDetailMission(null)} className="text-gray-400 hover:text-gray-600 shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {detailMission.description && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('missions.detail_description')}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{detailMission.description}</p>
                </div>
              )}

              {detailMission.attachmentUrls && detailMission.attachmentUrls.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('missions.detail_attachments')}</p>
                  <div className="space-y-1.5">
                    {detailMission.attachmentUrls.map((url) => {
                      const parts = url.split('/');
                      const filename = parts[parts.length - 1] || '';
                      const displayName = filename.length > 37 ? filename.substring(37) : filename;
                      const isPdf = /\.pdf$/i.test(url);
                      return (
                        <a
                          key={url}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 transition-colors"
                        >
                          {isPdf ? (
                            <FileText className="w-4 h-4 text-red-500 shrink-0" />
                          ) : (
                            <Image className="w-4 h-4 text-blue-500 shrink-0" />
                          )}
                          <span className="text-gray-700 truncate flex-1">{displayName}</span>
                          <ExternalLink className="w-3 h-3 text-gray-400 shrink-0" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('missions.detail_location')}</p>
                <p className="text-sm text-gray-700 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-gray-400" />
                  <span>
                    {detailMission.address}
                    {(detailMission.postalCode || detailMission.city) && (
                      <>
                        <br />
                        {detailMission.postalCode} {detailMission.city}
                      </>
                    )}
                    {detailMission.country && <> · {detailMission.country}</>}
                  </span>
                </p>
                {detailMission.address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${detailMission.address} ${detailMission.postalCode ?? ''} ${detailMission.city ?? ''}`,
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1"
                  >
                    <ExternalLink className="w-3 h-3" /> {t('missions.detail_open_in_maps')}
                  </a>
                )}
              </div>

              {(detailMission.clientName || detailMission.clientPhone || detailMission.clientEmail) && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('missions.detail_client')}</p>
                  <div className="text-sm text-gray-700 space-y-1">
                    {detailMission.clientName && <p className="font-medium">{detailMission.clientName}</p>}
                    {detailMission.clientContact && <p className="text-xs text-gray-600">{detailMission.clientContact}</p>}
                    {detailMission.clientPhone && (
                      <p className="flex items-center gap-1">
                        <Phone className="w-3 h-3 text-gray-400" />
                        <a href={`tel:${detailMission.clientPhone}`} className="text-blue-600 hover:underline">
                          {detailMission.clientPhone}
                        </a>
                      </p>
                    )}
                    {detailMission.clientEmail && (
                      <p className="flex items-center gap-1">
                        <Mail className="w-3 h-3 text-gray-400" />
                        <a href={`mailto:${detailMission.clientEmail}`} className="text-blue-600 hover:underline">
                          {detailMission.clientEmail}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('missions.detail_planning')}</p>
                <div className="text-sm text-gray-700 space-y-1">
                  <p className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" /> {formatDateTime(detailMission.scheduledAt)}
                  </p>
                  {detailMission.estimatedDurationMin && (
                    <p className="flex items-center gap-2 text-xs">
                      <Clock className="w-3 h-3 text-gray-400" /> {t('missions.estimated_duration_value', { min: detailMission.estimatedDurationMin })}
                    </p>
                  )}
                </div>
              </div>

              {/* Assignment block with inline edit */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('missions.detail_assignment')}</p>
                <div className="space-y-2">
                  <select
                    value={detailMission.assignedDriverId ?? ''}
                    onChange={(e) =>
                      handleAssign(
                        detailMission,
                        e.target.value || null,
                        detailMission.assignedDeviceId,
                      )
                    }
                    disabled={detailMission.status === 'COMPLETED' || detailMission.status === 'CANCELLED'}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="">{t('missions.choose_driver_placeholder')}</option>
                    {drivers.filter((d) => d.isActive && d.missionsEnabled).length === 0 && (
                      <option value="" disabled>
                        {t('missions.no_drivers_with_seat_inline')}
                      </option>
                    )}
                    {drivers
                      .filter((d) => d.isActive)
                      .map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                  </select>
                  <select
                    value={detailMission.assignedDeviceId ?? ''}
                    onChange={(e) =>
                      handleAssign(
                        detailMission,
                        detailMission.assignedDriverId,
                        e.target.value || null,
                      )
                    }
                    disabled={detailMission.status === 'COMPLETED' || detailMission.status === 'CANCELLED'}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="">{t('missions.choose_vehicle_placeholder')}</option>
                    {devices.length === 0 && (
                      <option value="" disabled>
                        {t('missions.no_vehicles_inline')}
                      </option>
                    )}
                    {devices.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name} {d.plate && `(${d.plate})`}
                      </option>
                    ))}
                  </select>
                  {detailMission.assignedBy && (
                    <p className="text-[10px] text-gray-500">
                      {t('missions.assigned_by', {
                        first: detailMission.assignedBy.firstName,
                        last: detailMission.assignedBy.lastName,
                        date: formatDateTime(detailMission.assignedAt),
                      })}
                    </p>
                  )}
                </div>
              </div>

              {/* Execution timeline */}
              {(detailMission.startedAt || detailMission.completedAt || detailMission.cancelledAt) && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">{t('missions.detail_execution')}</p>
                  <div className="text-xs text-gray-600 space-y-0.5">
                    {detailMission.startedAt && (
                      <p>▸ {t('missions.started_on', { date: formatDateTime(detailMission.startedAt) })}</p>
                    )}
                    {detailMission.completedAt && (
                      <p>✓ {t('missions.completed_on', { date: formatDateTime(detailMission.completedAt) })}</p>
                    )}
                    {detailMission.cancelledAt && (
                      <p>✗ {t('missions.cancelled_on', { date: formatDateTime(detailMission.cancelledAt) })}</p>
                    )}
                    {detailMission.cancelReason && (
                      <p className="text-red-600">{t('missions.cancel_reason', { reason: detailMission.cancelReason })}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Completion proof display — visible for COMPLETED missions AND
                  for IN_PROGRESS missions that have draft data already saved
                  by the driver. The dispatcher needs visibility on what the
                  driver has captured so far, even before final validation. */}
              {(() => {
                const hasContent =
                  !!detailMission.completionNotes ||
                  (detailMission.proofPhotoUrls && detailMission.proofPhotoUrls.length > 0) ||
                  !!detailMission.signatureUrl;
                if (!hasContent && detailMission.status !== 'COMPLETED') return null;

                const isDraft = detailMission.status === 'IN_PROGRESS';
                const isCompleted = detailMission.status === 'COMPLETED';

                return (
                  <div
                    className={`rounded-lg border p-3 ${
                      isDraft
                        ? 'bg-amber-50/40 border-amber-200'
                        : 'bg-green-50/30 border-green-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                        {t('missions.report_block_title')}
                      </p>
                      {isDraft && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full">
                          <AlertTriangle className="w-3 h-3" /> {t('missions.draft_badge')}
                        </span>
                      )}
                      {isCompleted && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-green-100 text-green-800 border border-green-300 px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="w-3 h-3" /> {t('missions.validated_badge', { date: formatDateTime(detailMission.completedAt) })}
                        </span>
                      )}
                    </div>

                    {isDraft && (
                      <p className="text-[11px] text-amber-700 italic mb-2">
                        {t('missions.draft_explanation')}
                      </p>
                    )}

                    {/* Intervention duration counter — visible on COMPLETED missions
                        (and as a running total on drafts, for informational purposes) */}
                    {detailMission.interventionDurationMs > 0 && (
                      <div className="mb-2 inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs">
                        <Clock className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-gray-500">
                          {isCompleted ? t('missions.total_intervention_duration') : t('missions.cumulative_duration')}
                        </span>
                        <span className="font-semibold text-gray-900">
                          {formatDuration(detailMission.interventionDurationMs)}
                        </span>
                      </div>
                    )}

                    {detailMission.completionNotes ? (
                      <div className="mb-2">
                        <p className="text-[10px] text-gray-500 uppercase mb-0.5">{t('missions.notes_label')}</p>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{detailMission.completionNotes}</p>
                      </div>
                    ) : (
                      isDraft && (
                        <p className="text-xs text-gray-500 italic mb-2">{t('missions.no_notes_yet')}</p>
                      )
                    )}

                    {detailMission.proofPhotoUrls && detailMission.proofPhotoUrls.length > 0 ? (
                      <div className="mb-2">
                        <p className="text-[10px] text-gray-500 uppercase mb-1">
                          {t('missions.photos_count', { count: detailMission.proofPhotoUrls.length })}
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {detailMission.proofPhotoUrls.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                              <img
                                src={url}
                                alt={`Photo ${i + 1}`}
                                className="w-full h-24 object-cover rounded border border-gray-200 hover:border-primary-400 transition-colors"
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : (
                      isDraft && (
                        <p className="text-xs text-gray-500 italic mb-2">{t('missions.no_photos_yet')}</p>
                      )
                    )}

                    {detailMission.signatureUrl ? (
                      <div className="bg-white border border-gray-200 rounded p-2">
                        <p className="text-[10px] text-gray-500 mb-1">
                          {t('missions.signature_label')}
                          {detailMission.signerName && (
                            <span className="ml-1 text-gray-700 font-medium">— {detailMission.signerName}</span>
                          )}
                        </p>
                        <img src={detailMission.signatureUrl} alt={t('missions.signature_label')} className="max-h-24" />
                      </div>
                    ) : (
                      isDraft && (
                        <p className="text-xs text-gray-500 italic">{t('missions.no_signature_yet')}</p>
                      )
                    )}
                  </div>
                );
              })()}

              {/* Action buttons */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200 flex-wrap">
                {/* Start / Finalize — RESTRICTED to the assigned driver only.
                    Dispatchers cannot perform the intervention on behalf of a
                    driver, only the driver who actually does the work can. */}
                {(detailMission.status === 'SCHEDULED' || detailMission.status === 'DRAFT') &&
                  isAssignedDriver(detailMission) && (
                    <button
                      onClick={() => handleStart(detailMission)}
                      disabled={busy === detailMission.id}
                      className="flex-1 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm flex items-center justify-center gap-2 font-medium"
                    >
                      {busy === detailMission.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                      {t('missions.btn_start')}
                    </button>
                  )}
                {detailMission.status === 'IN_PROGRESS' && isAssignedDriver(detailMission) && (() => {
                  const hasDraft =
                    !!detailMission.completionNotes ||
                    !!detailMission.signatureUrl ||
                    (detailMission.proofPhotoUrls && detailMission.proofPhotoUrls.length > 0);
                  return (
                    <button
                      onClick={() => openCompletionForm(detailMission)}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm flex items-center justify-center gap-2 font-medium"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {hasDraft ? t('missions.btn_resume_finalize') : t('missions.btn_complete')}
                    </button>
                  );
                })()}
                {/* Hint shown to non-driver users when the mission needs driver action */}
                {(detailMission.status === 'SCHEDULED' ||
                  detailMission.status === 'DRAFT' ||
                  detailMission.status === 'IN_PROGRESS') &&
                  !isAssignedDriver(detailMission) && (
                    <div className="flex-1 bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs text-gray-500 italic">
                      {detailMission.assignedDriver
                        ? t('missions.waiting_action_of', { driver: driverName(detailMission.assignedDriver) })
                        : t('missions.assign_driver_to_start')}
                    </div>
                  )}
                {/* Edit completed mission — for dispatchers to fix typos on the PDF */}
                {detailMission.status === 'COMPLETED' && (
                  <button
                    onClick={() => openEditForm(detailMission)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded text-sm flex items-center justify-center gap-2 font-medium"
                    title={t('missions.btn_edit_report_title')}
                  >
                    <Pencil className="w-4 h-4" /> {t('missions.btn_edit_report')}
                  </button>
                )}
                {detailMission.status !== 'COMPLETED' && detailMission.status !== 'CANCELLED' && (
                  <button
                    onClick={() => handleCancel(detailMission)}
                    disabled={busy === detailMission.id}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded text-sm flex items-center gap-2"
                  >
                    <Ban className="w-4 h-4" /> {t('missions.btn_cancel')}
                  </button>
                )}
              </div>

              {/* PDF report buttons — visible at any status (the report is always generatable) */}
              <div className="flex items-center gap-2 pt-2 mt-2 border-t border-gray-100 flex-wrap">
                <button
                  onClick={() => handleViewPdf(detailMission)}
                  disabled={pdfDownloading}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 text-gray-800 px-4 py-2 rounded text-sm flex items-center justify-center gap-2 font-medium"
                  title={t('missions.btn_view_pdf_title')}
                >
                  {pdfDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  {t('missions.btn_view_pdf')}
                </button>
                <button
                  onClick={() => handleDownloadPdf(detailMission)}
                  disabled={pdfDownloading}
                  className="bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 text-gray-800 px-3 py-2 rounded text-sm flex items-center justify-center gap-1.5"
                  title={t('missions.btn_download_pdf_title')}
                >
                  <FileDown className="w-4 h-4" />
                </button>
                <button
                  onClick={() => openSendPdfModal(detailMission)}
                  className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded text-sm flex items-center justify-center gap-2 font-medium"
                  title={detailMission.clientEmail ? t('missions.btn_send_to_client_with_email', { email: detailMission.clientEmail }) : t('missions.btn_send_to_client_default')}
                >
                  <Send className="w-4 h-4" /> {t('missions.btn_send_to_client')}
                </button>
              </div>

              {/* Live tracking link for the client — only when a vehicle is assigned
                  and the mission is still actionable (not completed/cancelled). */}
              {detailMission.assignedDeviceId &&
                detailMission.status !== 'COMPLETED' &&
                detailMission.status !== 'CANCELLED' && (
                  <div className="flex items-center gap-2 pt-2 mt-2 border-t border-gray-100">
                    <button
                      onClick={() => setShareLinkMission(detailMission)}
                      className="flex-1 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 px-4 py-2 rounded text-sm flex items-center justify-center gap-2 font-medium"
                      title={t('missions.tracking_link_tooltip')}
                    >
                      <Share2 className="w-4 h-4" /> {t('missions.actions.send_tracking_link')}
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Share-link modal (mission mode — recipient pre-filled, expires with mission) */}
      {shareLinkMission && shareLinkMission.assignedDeviceId && (
        <ShareLinkModal
          open={true}
          onClose={() => setShareLinkMission(null)}
          deviceId={shareLinkMission.assignedDeviceId}
          deviceName={shareLinkMission.assignedDevice?.name || t('missions.vehicle_fallback')}
          missionId={shareLinkMission.id}
          defaultRecipientPhone={shareLinkMission.clientPhone || null}
          defaultRecipientEmail={shareLinkMission.clientEmail || null}
          defaultClientName={shareLinkMission.clientName || null}
        />
      )}

      {/* Completion form — proof upload + signature */}
      {showCompletionForm && detailMission && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => setShowCompletionForm(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" /> {t('missions.completion_modal_title', { ref: detailMission.reference })}
              </h3>
              <button onClick={() => setShowCompletionForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.completion_notes_label')}</label>
                <textarea
                  rows={3}
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder={t('missions.completion.notes_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.completion_photos_label')}</label>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {proofPhotos.map((url) => (
                    <div key={url} className="relative group">
                      <img src={url} alt="" className="w-full h-24 object-cover rounded border border-gray-200" />
                      <button
                        onClick={() => removeProofPhoto(url)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 flex items-center justify-center gap-2"
                >
                  <Camera className="w-4 h-4" /> {t('missions.completion_add_photos')}
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('missions.completion_signer_label')}</label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder={t('missions.completion_signer_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <PenLine className="w-3 h-3" /> {t('missions.completion_signature_label')}
                  </span>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="text-[10px] text-gray-500 hover:text-red-600"
                  >
                    {t('missions.completion_clear_signature')}
                  </button>
                </label>
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={180}
                  className="w-full border-2 border-gray-300 rounded bg-white touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <p className="text-[10px] text-gray-500 mt-1">{t('missions.completion_signature_hint')}</p>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 shadow-[0_-4px_8px_-4px_rgba(0,0,0,0.05)]">
              <button
                onClick={() => setShowCompletionForm(false)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 rounded"
              >
                {t('missions.completion_cancel')}
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={busy === detailMission.id}
                className="bg-amber-100 hover:bg-amber-200 disabled:bg-gray-200 text-amber-800 px-4 py-2 rounded text-sm flex items-center justify-center gap-2 font-medium"
                title={t('missions.completion_save_draft_title')}
              >
                {busy === detailMission.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('missions.completion_save_draft')}
              </button>
              <button
                onClick={handleComplete}
                disabled={busy === detailMission.id}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm flex items-center justify-center gap-2 font-semibold"
                title={t('missions.completion_finalize_title')}
              >
                {busy === detailMission.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {t('missions.completion_finalize')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send PDF modal */}
      {sendPdfMission && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setSendPdfMission(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Send className="w-4 h-4 text-blue-600" /> {t('missions.send_pdf_modal_title')}
              </h3>
              <button onClick={() => setSendPdfMission(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
                <p className="font-mono font-semibold">{sendPdfMission.reference}</p>
                <p className="line-clamp-1">{sendPdfMission.title}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('missions.send_pdf_recipient_label')}
                </label>
                <input
                  type="email"
                  value={sendPdfRecipient}
                  onChange={(e) => setSendPdfRecipient(e.target.value)}
                  placeholder={t('missions.send_pdf_recipient_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
                {!sendPdfMission.clientEmail && (
                  <p className="mt-1 text-[10px] text-amber-700">
                    {t('missions.send_pdf_no_client_email')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('missions.send_pdf_message_label')}
                </label>
                <textarea
                  rows={4}
                  value={sendPdfMessage}
                  onChange={(e) => setSendPdfMessage(e.target.value)}
                  placeholder={t('missions.send_pdf_message_placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setSendPdfMission(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                {t('missions.send_pdf_cancel')}
              </button>
              <button
                onClick={handleSendPdf}
                disabled={sendPdfBusy}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"
              >
                {sendPdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t('missions.send_pdf_send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
